import { Injectable, OnDestroy, inject } from '@angular/core';
import { ReplaySubject, Subject, filter, firstValueFrom, map, merge, switchMap, take, takeUntil, timeout } from 'rxjs';
import { InboxEventType, InboxRoomMessage, SocketRooms } from '@jms/model';
import { RoomConnection, SharedSocketService } from '@workbuddy/services';

export type InboxRoomConnection = RoomConnection<InboxRoomMessage>;

@Injectable({ providedIn: 'root' })
export class InboxRoomService implements OnDestroy {

    private readonly socket = inject(SharedSocketService);
    private readonly destroy$ = new Subject<void>();
    private readonly room$ = new ReplaySubject<InboxRoomConnection>(1);
    private _room: InboxRoomConnection | null = null;
    public readonly events$ = merge(
        this.room$.pipe(switchMap(room => room.events$)),
        this.socket.messages$.pipe(
            filter((message): message is InboxRoomMessage => this.isTeamInboxMessage(message)),
            map(message => message as InboxRoomMessage)
        )
    );

    public constructor() {

        this.ensureRoom();
        this.socket.connected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.ensureRoom());
    }

    public ngOnDestroy(): void {

        this.leave();
        this.destroy$.next();
        this.destroy$.complete();
        this.room$.complete();
    }

    public get room(): InboxRoomConnection | null {

        return this._room;
    }

    public async dispatch<T>(action: string, payload: unknown): Promise<T> {

        const room = await this.requireRoom();
        return room.dispatch<T>(action, payload);
    }

    private async requireRoom(): Promise<InboxRoomConnection> {

        const room = this.ensureRoom();
        if (room) return room;

        return firstValueFrom(this.socket.connected$.pipe(
            map(() => this.ensureRoom()),
            filter((room): room is InboxRoomConnection => !!room),
            take(1),
            timeout({ first: 10000 })
        ));
    }

    private ensureRoom(): InboxRoomConnection | null {

        const userId = this.socket.userId;
        if (!userId) return null;

        const roomKey = SocketRooms.inbox(userId);
        if (this._room?.key.key === roomKey.key) return this._room;

        if (!this._room) {
            this._room = this.socket.join(roomKey);
        } else {
            this.leave();
            this._room = this.socket.join(roomKey);
        }

        this.room$.next(this._room);
        return this._room;
    }

    private leave(): void {

        this._room?.leave();
        this._room = null;
    }

    private isTeamInboxMessage(message: unknown): boolean {

        if (!message || typeof message !== 'object') return false;

        const value = message as { __room?: unknown; type?: unknown };
        return typeof value.__room === 'string' && value.__room.startsWith('team:') && value.type === InboxEventType.QueueNavigationInvalidated;
    }
}
